import {
  ChevronDownIcon,
  ChevronsLeftRightEllipsisIcon,
  PlusIcon,
  QrCodeIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  AuthAccessReadScope,
  AuthAccessWriteScope,
  AuthOrchestrationOperateScope,
  AuthOrchestrationReadScope,
  AuthReviewWriteScope,
  AuthStandardClientScopes,
  type AuthClientSession,
  type AuthEnvironmentScope,
  type AuthPairingLink,
  type EnvironmentId,
} from "@t3tools/contracts";
import { WsRpcClient } from "@t3tools/client-runtime";
import * as DateTime from "effect/DateTime";
import type { MessageDescriptor } from "@lingui/core";
import { msg, plural, t } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { cn } from "../../lib/utils";
import { formatElapsedDurationLabel, formatExpiresInLabel } from "../../timestampFormat";
import {
  SettingsPageContainer,
  SettingsRow,
  SettingsSection,
  useRelativeTimeTick,
} from "./settingsLayout";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogFooter,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { QRCodeSvg } from "../ui/qr-code";
import { Switch } from "../ui/switch";
import { stackedThreadToast, toastManager } from "../ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { Button } from "../ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../ui/empty";
import { Group, GroupSeparator } from "../ui/group";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { Textarea } from "../ui/textarea";
import { getPairingTokenFromUrl, setPairingTokenOnUrl } from "../../pairingUrl";
import { readHostedPairingRequest } from "../../hostedPairing";
import {
  createServerPairingCredential,
  revokeServerClientSession,
  revokeServerPairingLink,
  isLoopbackHostname,
  usePrimarySessionState,
  type ServerClientSessionRecord,
  type ServerPairingLinkRecord,
} from "~/environments/primary";
import {
  type SavedEnvironmentRecord,
  type SavedEnvironmentRuntimeState,
  useSavedEnvironmentRegistryStore,
  useSavedEnvironmentRuntimeStore,
  addSavedEnvironment,
  disconnectSavedEnvironment,
  getPrimaryEnvironmentConnection,
  reconnectSavedEnvironment,
  removeSavedEnvironment,
} from "~/environments/runtime";
import { resolveServerConfigVersionMismatch } from "~/versionSkew";
import { useServerConfig } from "~/rpc/serverState";

const accessTimestampFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatAccessTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return accessTimestampFormatter.format(parsed);
}

const PAIRING_MAX_USES_OPTIONS = [1] as const;

const PAIRING_SCOPE_OPTIONS: ReadonlyArray<{
  readonly scope: AuthEnvironmentScope;
  readonly title: MessageDescriptor;
  readonly description: MessageDescriptor;
}> = [
  {
    scope: AuthOrchestrationReadScope,
    title: msg`View environment`,
    description: msg`Read threads, status, diffs, and configuration.`,
  },
  {
    scope: AuthOrchestrationOperateScope,
    title: msg`Operate tasks`,
    description: msg`Start tasks and perform changes in the environment.`,
  },
  {
    scope: AuthReviewWriteScope,
    title: msg`Write reviews`,
    description: msg`Create comments while reviewing changes.`,
  },
  {
    scope: AuthAccessReadScope,
    title: msg`View access`,
    description: msg`Inspect pairing links and authorized clients.`,
  },
  {
    scope: AuthAccessWriteScope,
    title: msg`Manage access`,
    description: msg`Issue and revoke credentials for other clients.`,
  },
];

const PAIRING_TTL_DAY_OPTIONS: ReadonlyArray<number> = [3, 7, 30];
const SECONDS_PER_DAY = 86_400;

function AccessScopeSummary({
  scopes,
  label,
}: {
  readonly scopes: ReadonlyArray<AuthEnvironmentScope>;
  readonly label: string;
}) {
  const { t } = useLingui();
  const scopeCountLabel = plural(scopes.length, { one: "# scope", other: "# scopes" });

  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={250}
        closeDelay={100}
        render={
          <button
            type="button"
            aria-label={t`${label}: show ${scopeCountLabel}`}
            className="cursor-help underline decoration-border underline-offset-2 outline-hidden hover:text-foreground focus-visible:text-foreground"
          />
        }
      >
        {scopeCountLabel}
      </PopoverTrigger>
      <PopoverPopup
        side="top"
        align="start"
        tooltipStyle
        className="w-max max-w-80 whitespace-normal"
      >
        <p className="mb-1 font-medium">
          <Trans>Granted scopes</Trans>
        </p>
        <div className="flex flex-col gap-0.5">
          {scopes.map((scope) => (
            <code key={scope} className="font-mono text-foreground/85">
              {scope}
            </code>
          ))}
        </div>
      </PopoverPopup>
    </Popover>
  );
}

type ConnectionStatusDotProps = {
  tooltipText?: string | null;
  dotClassName: string;
  pingClassName?: string | null;
};

function ConnectionStatusDot({
  tooltipText,
  dotClassName,
  pingClassName,
}: ConnectionStatusDotProps) {
  const dotContent = (
    <>
      {pingClassName ? (
        <span
          className={cn(
            "absolute inline-flex h-full w-full animate-ping rounded-full",
            pingClassName,
          )}
        />
      ) : null}
      <span className={cn("relative inline-flex size-2 rounded-full", dotClassName)} />
    </>
  );

  if (!tooltipText) {
    return (
      <span className="relative flex size-3 shrink-0 items-center justify-center">
        {dotContent}
      </span>
    );
  }

  const dot = (
    <button
      type="button"
      aria-label={tooltipText}
      className="relative flex size-3 shrink-0 cursor-help items-center justify-center rounded-full outline-hidden"
    >
      {dotContent}
    </button>
  );

  return (
    <Tooltip>
      <TooltipTrigger render={dot} />
      <TooltipPopup side="top" className="max-w-80 whitespace-pre-wrap leading-tight">
        {tooltipText}
      </TooltipPopup>
    </Tooltip>
  );
}

function getSavedBackendStatusTooltip(
  runtime: SavedEnvironmentRuntimeState | null,
  record: SavedEnvironmentRecord,
  nowMs: number,
) {
  const connectionState = runtime?.connectionState ?? "disconnected";

  if (connectionState === "connected") {
    const connectedAt = runtime?.connectedAt ?? record.lastConnectedAt;
    return connectedAt ? t`Connected for ${formatElapsedDurationLabel(connectedAt, nowMs)}` : null;
  }

  if (connectionState === "connecting") {
    return null;
  }

  if (connectionState === "error") {
    return runtime?.lastError ?? t`An unknown connection error occurred.`;
  }

  return record.lastConnectedAt
    ? t`Last connected at ${formatAccessTimestamp(record.lastConnectedAt)}`
    : t`Not connected yet.`;
}

function parsePairingUrlFields(
  input: string,
): { readonly host: string; readonly pairingCode: string } | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  try {
    const urlLikeInput =
      /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//u.test(trimmed) || trimmed.startsWith("//")
        ? trimmed
        : `https://${trimmed}`;
    const url = new URL(urlLikeInput, window.location.origin);
    const hostedPairingRequest = readHostedPairingRequest(url);
    if (hostedPairingRequest) {
      return {
        host: hostedPairingRequest.host,
        pairingCode: hostedPairingRequest.token,
      };
    }

    const pairingCode = getPairingTokenFromUrl(url);
    if (!pairingCode) return null;
    return {
      host: url.origin,
      pairingCode,
    };
  } catch {
    return null;
  }
}

function parseRemotePairingFields(input: { readonly host: string; readonly pairingCode: string }): {
  readonly host: string;
  readonly pairingCode: string;
} {
  const parsedPairingUrl = parsePairingUrlFields(input.host);
  if (parsedPairingUrl) return parsedPairingUrl;

  const host = input.host.trim();
  const pairingCode = input.pairingCode.trim();
  if (!host) {
    throw new Error(t`Enter a backend host.`);
  }
  if (!pairingCode) {
    throw new Error(t`Enter a pairing code.`);
  }
  return { host, pairingCode };
}

/** Direct row in the card – same pattern as the Provider / ACP-agent list rows. */
const ITEM_ROW_CLASSNAME = "border-t border-border/60 px-4 py-4 first:border-t-0 sm:px-5";

const ITEM_ROW_INNER_CLASSNAME =
  "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between";

function sortPairingLinks(links: ReadonlyArray<ServerPairingLinkRecord>) {
  return [...links].toSorted(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );
}

function sortClientSessions(sessions: ReadonlyArray<ServerClientSessionRecord>) {
  return [...sessions].toSorted((left, right) => {
    if (left.current !== right.current) {
      return left.current ? -1 : 1;
    }
    if (left.connected !== right.connected) {
      return left.connected ? -1 : 1;
    }
    return new Date(right.issuedAt).getTime() - new Date(left.issuedAt).getTime();
  });
}

function toPairingLinkRecord(pairingLink: AuthPairingLink): ServerPairingLinkRecord {
  return {
    ...pairingLink,
    createdAt: DateTime.formatIso(pairingLink.createdAt),
    expiresAt: pairingLink.expiresAt === null ? null : DateTime.formatIso(pairingLink.expiresAt),
    lastUsedAt: pairingLink.lastUsedAt === null ? null : DateTime.formatIso(pairingLink.lastUsedAt),
  };
}

function toClientSessionRecord(clientSession: AuthClientSession): ServerClientSessionRecord {
  return {
    ...clientSession,
    issuedAt: DateTime.formatIso(clientSession.issuedAt),
    expiresAt: DateTime.formatIso(clientSession.expiresAt),
    lastConnectedAt:
      clientSession.lastConnectedAt === null
        ? null
        : DateTime.formatIso(clientSession.lastConnectedAt),
  };
}

function upsertPairingLink(
  current: ReadonlyArray<ServerPairingLinkRecord>,
  next: ServerPairingLinkRecord,
) {
  const existingIndex = current.findIndex((pairingLink) => pairingLink.id === next.id);
  if (existingIndex === -1) {
    return sortPairingLinks([...current, next]);
  }
  const updated = [...current];
  updated[existingIndex] = next;
  return sortPairingLinks(updated);
}

function removePairingLink(current: ReadonlyArray<ServerPairingLinkRecord>, id: string) {
  return current.filter((pairingLink) => pairingLink.id !== id);
}

function upsertClientSession(
  current: ReadonlyArray<ServerClientSessionRecord>,
  next: ServerClientSessionRecord,
) {
  const existingIndex = current.findIndex(
    (clientSession) => clientSession.sessionId === next.sessionId,
  );
  if (existingIndex === -1) {
    return sortClientSessions([...current, next]);
  }
  const updated = [...current];
  updated[existingIndex] = next;
  return sortClientSessions(updated);
}

function removeClientSession(
  current: ReadonlyArray<ServerClientSessionRecord>,
  sessionId: ServerClientSessionRecord["sessionId"],
) {
  return current.filter((clientSession) => clientSession.sessionId !== sessionId);
}

function resolveCurrentOriginPairingUrl(credential: string): string {
  const url = new URL("/pair", window.location.href);
  return setPairingTokenOnUrl(url, credential).toString();
}

type PairingLinkListRowProps = {
  pairingLink: ServerPairingLinkRecord;
  /** True when a session opened with this link is currently connected. */
  isConnected?: boolean;
  revokingPairingLinkId: string | null;
  onRevoke: (id: string) => void;
};

const PairingLinkListRow = memo(function PairingLinkListRow({
  pairingLink,
  isConnected = false,
  revokingPairingLinkId,
  onRevoke,
}: PairingLinkListRowProps) {
  const { t } = useLingui();
  const nowMs = useRelativeTimeTick(1_000);
  const expiresAtMs = useMemo(
    () => (pairingLink.expiresAt === null ? null : new Date(pairingLink.expiresAt).getTime()),
    [pairingLink.expiresAt],
  );
  const [isRevealDialogOpen, setIsRevealDialogOpen] = useState(false);

  const currentOriginPairingUrl = useMemo(
    () => resolveCurrentOriginPairingUrl(pairingLink.credential),
    [pairingLink.credential],
  );
  const shareablePairingUrl = isLoopbackHostname(window.location.hostname)
    ? null
    : currentOriginPairingUrl;
  const revealValue = shareablePairingUrl ?? pairingLink.credential;
  const canCopyToClipboard =
    typeof window !== "undefined" &&
    window.isSecureContext &&
    navigator.clipboard?.writeText != null;

  const { copyToClipboard } = useCopyToClipboard<"code" | "link">({
    onCopy: (kind) => {
      toastManager.add({
        type: "success",
        title: kind === "link" ? t`Pairing URL copied` : t`Pairing code copied`,
        description:
          kind === "link"
            ? t`Open it in the client you want to pair to this environment.`
            : t`Paste it into another client to finish pairing.`,
      });
    },
    onError: (error, kind) => {
      setIsRevealDialogOpen(true);
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: canCopyToClipboard
            ? kind === "link"
              ? t`Could not copy pairing URL`
              : t`Could not copy pairing code`
            : t`Clipboard copy unavailable`,
          description: canCopyToClipboard ? error.message : t`Showing the full value instead.`,
        }),
      );
    },
  });

  const copyPairingValue = useCallback(
    (value: string, kind: "code" | "link") => {
      copyToClipboard(value, kind);
    },
    [copyToClipboard],
  );

  const handleCopyCode = useCallback(() => {
    copyPairingValue(pairingLink.credential, "code");
  }, [copyPairingValue, pairingLink.credential]);

  const handleCopyDefaultLink = useCallback(() => {
    if (!shareablePairingUrl) return;
    copyPairingValue(shareablePairingUrl, "link");
  }, [copyPairingValue, shareablePairingUrl]);

  const expiresAbsolute =
    pairingLink.expiresAt === null
      ? t`Never expires`
      : formatAccessTimestamp(pairingLink.expiresAt);
  const expiresLabel =
    pairingLink.expiresAt === null
      ? t`Never expires`
      : formatExpiresInLabel(pairingLink.expiresAt, nowMs);
  const lastUsedElapsed =
    pairingLink.lastUsedAt === null
      ? null
      : formatElapsedDurationLabel(pairingLink.lastUsedAt, nowMs);
  const lastUsedLabel =
    lastUsedElapsed === null
      ? null
      : lastUsedElapsed === t`just now`
        ? lastUsedElapsed
        : t`${lastUsedElapsed} ago`;
  const kindLabel =
    pairingLink.maxUses === null
      ? t`Reusable`
      : pairingLink.maxUses === 1
        ? t`One-time`
        : t`Up to ${pairingLink.maxUses} devices`;
  const useCountLabel =
    pairingLink.maxUses === null
      ? t`Used ${pairingLink.useCount}×`
      : t`Used ${pairingLink.useCount} of ${pairingLink.maxUses}`;
  const usageLabel =
    lastUsedLabel === null ? t`Never used` : t`${useCountLabel} · ${lastUsedLabel}`;

  const primaryLabel = pairingLink.label ?? t`Pairing link`;
  const renderPairingCodeMenuItem = () => (
    <MenuItem onClick={handleCopyCode}>
      <Trans>Copy code</Trans>
    </MenuItem>
  );

  if (expiresAtMs !== null && expiresAtMs <= nowMs) {
    return null;
  }

  return (
    <div className={ITEM_ROW_CLASSNAME}>
      <div className={ITEM_ROW_INNER_CLASSNAME}>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-h-5 items-center gap-1.5">
            <ConnectionStatusDot
              tooltipText={
                isConnected
                  ? t`A client paired with this link is connected now`
                  : t`Link created at ${formatAccessTimestamp(pairingLink.createdAt)}`
              }
              dotClassName={isConnected ? "bg-success" : "bg-amber-400"}
              pingClassName={isConnected ? "bg-success/60 duration-2000" : null}
            />
            <h3 className="text-sm font-medium text-foreground">{primaryLabel}</h3>
            <Popover>
              {shareablePairingUrl ? (
                <>
                  <PopoverTrigger
                    openOnHover
                    delay={250}
                    closeDelay={100}
                    render={
                      <button
                        type="button"
                        className="inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground/50 outline-none hover:text-foreground"
                        aria-label={t`Show QR code`}
                      />
                    }
                  >
                    <QrCodeIcon aria-hidden className="size-3" />
                  </PopoverTrigger>
                  <PopoverPopup side="top" align="start" tooltipStyle className="w-max">
                    <QRCodeSvg
                      value={shareablePairingUrl}
                      size={88}
                      level="M"
                      marginSize={2}
                      title={t`Pairing link — scan to open on another device`}
                    />
                  </PopoverPopup>
                </>
              ) : null}
            </Popover>
          </div>
          <Tooltip>
            <TooltipTrigger
              render={<p aria-label={expiresAbsolute} className="text-xs text-muted-foreground" />}
            >
              {kindLabel}
              <span aria-hidden> · </span>
              {expiresLabel}
              <span aria-hidden> · </span>
              {usageLabel}
              <span aria-hidden> · </span>
              <AccessScopeSummary scopes={pairingLink.scopes} label={t`Pairing link scopes`} />
            </TooltipTrigger>
            <TooltipPopup side="top">{expiresAbsolute}</TooltipPopup>
          </Tooltip>
          {shareablePairingUrl === null ? (
            <p className="text-[11px] text-muted-foreground/70">
              <Trans>
                Copy the token and pair from another client using this backend&apos;s reachable
                host.
              </Trans>
            </p>
          ) : null}
        </div>
        <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto sm:justify-end">
          <Dialog open={isRevealDialogOpen} onOpenChange={setIsRevealDialogOpen}>
            {canCopyToClipboard ? (
              <>
                {shareablePairingUrl ? (
                  <Group aria-label={t`Copy pairing link`}>
                    <Button size="xs" variant="outline" onClick={handleCopyDefaultLink}>
                      <Trans>Copy pairing URL</Trans>
                    </Button>
                    <GroupSeparator />
                    <Menu>
                      <MenuTrigger
                        render={
                          <Button
                            size="icon-xs"
                            variant="outline"
                            aria-label={t`More copy options`}
                          />
                        }
                      >
                        <ChevronDownIcon className="size-3.5" />
                      </MenuTrigger>
                      <MenuPopup align="end" className="min-w-40">
                        {renderPairingCodeMenuItem()}
                      </MenuPopup>
                    </Menu>
                  </Group>
                ) : (
                  <Button size="xs" variant="outline" onClick={handleCopyCode}>
                    <Trans>Copy code</Trans>
                  </Button>
                )}
              </>
            ) : (
              <DialogTrigger render={<Button size="xs" variant="outline" />}>
                {shareablePairingUrl ? <Trans>Show link</Trans> : <Trans>Show code</Trans>}
              </DialogTrigger>
            )}
            <DialogPopup className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {shareablePairingUrl ? <Trans>Pairing link</Trans> : <Trans>Pairing code</Trans>}
                </DialogTitle>
                <DialogDescription>
                  {shareablePairingUrl ? (
                    <Trans>
                      Clipboard copy is unavailable here. Open or manually copy this full pairing
                      URL on the device you want to connect.
                    </Trans>
                  ) : (
                    <Trans>
                      Clipboard copy is unavailable here. Manually copy this code into another
                      client.
                    </Trans>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogPanel className="space-y-4">
                <Textarea
                  readOnly
                  value={revealValue}
                  rows={shareablePairingUrl ? 4 : 3}
                  className="text-xs leading-relaxed"
                  onFocus={(event) => event.currentTarget.select()}
                  onClick={(event) => event.currentTarget.select()}
                />
                {shareablePairingUrl ? (
                  <div className="flex justify-center rounded-xl border border-border/60 bg-muted/30 p-4">
                    <QRCodeSvg
                      value={shareablePairingUrl}
                      size={132}
                      level="M"
                      marginSize={2}
                      title={t`Pairing link — scan to open on another device`}
                    />
                  </div>
                ) : null}
              </DialogPanel>
              <DialogFooter variant="bare">
                <Button variant="outline" onClick={() => setIsRevealDialogOpen(false)}>
                  <Trans>Done</Trans>
                </Button>
                {canCopyToClipboard ? (
                  <Button variant="outline" size="xs" onClick={handleCopyCode}>
                    <Trans>Copy code</Trans>
                  </Button>
                ) : null}
              </DialogFooter>
            </DialogPopup>
          </Dialog>
          <Button
            size="xs"
            variant="destructive-outline"
            disabled={revokingPairingLinkId === pairingLink.id}
            onClick={() => void onRevoke(pairingLink.id)}
          >
            {revokingPairingLinkId === pairingLink.id ? (
              <Trans>Revoking…</Trans>
            ) : (
              <Trans>Revoke</Trans>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
});

type ConnectedClientListRowProps = {
  clientSession: ServerClientSessionRecord;
  /** Renders the row indented beneath the pairing link that created it. */
  nested?: boolean;
  revokingClientSessionId: string | null;
  /** Absent for sessions that belong to a pairing link; those go with the link. */
  onRevokeSession?: (sessionId: ServerClientSessionRecord["sessionId"]) => void;
};

const ConnectedClientListRow = memo(function ConnectedClientListRow({
  clientSession,
  nested = false,
  revokingClientSessionId,
  onRevokeSession,
}: ConnectedClientListRowProps) {
  const { t } = useLingui();
  const nowMs = useRelativeTimeTick(1_000);
  const isLive = clientSession.current || clientSession.connected;
  const lastConnectedAt = clientSession.lastConnectedAt;
  const statusTooltip = isLive
    ? lastConnectedAt
      ? t`Connected for ${formatElapsedDurationLabel(lastConnectedAt, nowMs)}`
      : t`Connected`
    : lastConnectedAt
      ? t`Last connected at ${formatAccessTimestamp(lastConnectedAt)}`
      : t`Not connected yet.`;
  const deviceInfoBits = [
    clientSession.client.deviceType !== "unknown"
      ? clientSession.client.deviceType[0]?.toUpperCase() + clientSession.client.deviceType.slice(1)
      : null,
    clientSession.client.os ?? null,
    clientSession.client.browser ?? null,
    clientSession.client.ipAddress ?? null,
  ].filter((value): value is string => value !== null);
  const primaryLabel =
    clientSession.client.label ??
    ([clientSession.client.os, clientSession.client.browser].filter(Boolean).join(" · ") ||
      clientSession.subject);

  return (
    <div
      className={cn(
        ITEM_ROW_CLASSNAME,
        nested && "border-l-2 border-l-border/70 py-3 pl-7 sm:pl-8",
      )}
    >
      <div className={ITEM_ROW_INNER_CLASSNAME}>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-h-5 items-center gap-1.5">
            <ConnectionStatusDot
              tooltipText={statusTooltip}
              dotClassName={isLive ? "bg-success" : "bg-muted-foreground/30"}
              pingClassName={isLive ? "bg-success/60 duration-2000" : null}
            />
            <h3 className="text-sm font-medium text-foreground">{primaryLabel}</h3>
            {clientSession.current ? (
              <span className="text-[10px] text-muted-foreground/80 rounded-md border border-border/50 bg-muted/50 px-1 py-0.5">
                <Trans>This device</Trans>
              </span>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            {deviceInfoBits.length > 0 ? (
              <>
                {deviceInfoBits.join(" · ")}
                <span aria-hidden> · </span>
              </>
            ) : null}
            <AccessScopeSummary scopes={clientSession.scopes} label={t`Client scopes`} />
          </p>
        </div>
        <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto sm:justify-end">
          {onRevokeSession && !clientSession.current ? (
            <Button
              size="xs"
              variant="destructive-outline"
              disabled={revokingClientSessionId === clientSession.sessionId}
              onClick={() => void onRevokeSession(clientSession.sessionId)}
            >
              {revokingClientSessionId === clientSession.sessionId ? (
                <Trans>Revoking…</Trans>
              ) : (
                <Trans>Revoke</Trans>
              )}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
});

const AuthorizedClientsHeaderAction = memo(function AuthorizedClientsHeaderAction() {
  const { t, i18n } = useLingui();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pairingLabel, setPairingLabel] = useState("");
  const [pairingScopes, setPairingScopes] = useState<ReadonlyArray<AuthEnvironmentScope>>([
    ...AuthStandardClientScopes,
  ]);
  const [pairingTtlDays, setPairingTtlDays] = useState<number | null>(null);
  const [pairingMaxUses, setPairingMaxUses] = useState<number | null>(1);
  const [isCreatingPairingLink, setIsCreatingPairingLink] = useState(false);

  const handleCreatePairingLink = useCallback(async () => {
    setIsCreatingPairingLink(true);
    try {
      await createServerPairingCredential({
        label: pairingLabel,
        scopes: pairingScopes,
        ...(pairingTtlDays !== null ? { ttlSeconds: pairingTtlDays * SECONDS_PER_DAY } : {}),
        ...(pairingMaxUses !== null ? { maxUses: pairingMaxUses } : {}),
      });
      setPairingLabel("");
      setPairingScopes([...AuthStandardClientScopes]);
      setPairingTtlDays(null);
      setPairingMaxUses(1);
      setDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to create pairing URL.`;
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: t`Could not create pairing URL`,
          description: message,
        }),
      );
    } finally {
      setIsCreatingPairingLink(false);
    }
  }, [pairingLabel, pairingMaxUses, pairingScopes, pairingTtlDays, t]);

  const togglePairingScope = useCallback((scope: AuthEnvironmentScope, checked: boolean) => {
    setPairingScopes((current) =>
      checked ? [...current, scope] : current.filter((currentScope) => currentScope !== scope),
    );
  }, []);

  return (
    <div className="flex items-center gap-2">
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setPairingLabel("");
            setPairingScopes([...AuthStandardClientScopes]);
            setPairingTtlDays(null);
            setPairingMaxUses(1);
          }
        }}
      >
        <DialogTrigger
          render={
            <Button size="xs" variant="default">
              <PlusIcon className="size-3" />
              <Trans>Create link</Trans>
            </Button>
          }
        />
        <DialogPopup className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Trans>Create pairing link</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                Generate a link that pairs devices with this backend as an authorized client.
              </Trans>
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-5">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-foreground">
                <Trans>Client label (optional)</Trans>
              </span>
              <Input
                value={pairingLabel}
                onChange={(event) => setPairingLabel(event.target.value)}
                placeholder={t`e.g. Living room iPad`}
                disabled={isCreatingPairingLink}
                autoFocus
              />
            </label>
            <section className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-xs font-medium text-foreground">
                    <Trans>Permissions</Trans>
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    <Trans>Limit what the paired client can do.</Trans>
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={isCreatingPairingLink}
                    onClick={() => setPairingScopes([AuthOrchestrationReadScope])}
                  >
                    <Trans>Read only</Trans>
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={isCreatingPairingLink}
                    onClick={() => setPairingScopes([...AuthStandardClientScopes])}
                  >
                    <Trans>Standard</Trans>
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-border/60 rounded-lg border border-input bg-muted/25">
                {PAIRING_SCOPE_OPTIONS.map(({ scope, title, description }) => (
                  <label
                    key={scope}
                    className="flex cursor-pointer items-start gap-3 px-3 py-2.5 transition-colors hover:bg-muted/40"
                  >
                    <Checkbox
                      className="mt-0.5"
                      checked={pairingScopes.includes(scope)}
                      disabled={isCreatingPairingLink}
                      onCheckedChange={(checked) => togglePairingScope(scope, checked === true)}
                    />
                    <span className="min-w-0">
                      <span className="block text-xs font-medium text-foreground">
                        {i18n._(title)}
                      </span>
                      <span className="block text-xs leading-snug text-muted-foreground">
                        {i18n._(description)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {pairingScopes.length === 0 ? (
                <p className="text-xs text-destructive">
                  <Trans>Select at least one permission.</Trans>
                </p>
              ) : pairingScopes.includes(AuthAccessWriteScope) ? (
                <p className="text-xs text-warning">
                  <Trans>This client can create or revoke access for other devices.</Trans>
                </p>
              ) : null}
            </section>
            <section className="space-y-1.5">
              <div>
                <h3 className="text-xs font-medium text-foreground">
                  <Trans>Devices</Trans>
                </h3>
                <p className="text-xs text-muted-foreground">
                  <Trans>How many devices can pair with this link.</Trans>
                </p>
              </div>
              <Select
                value={pairingMaxUses === null ? "unlimited" : String(pairingMaxUses)}
                onValueChange={(value) => {
                  setPairingMaxUses(value === "unlimited" ? null : Number(value));
                }}
              >
                <SelectTrigger
                  className="w-full"
                  aria-label={t`Devices`}
                  disabled={isCreatingPairingLink}
                >
                  <SelectValue>
                    {pairingMaxUses === null
                      ? t`Any number of devices`
                      : plural(pairingMaxUses, { one: "# device", other: "# devices" })}
                  </SelectValue>
                </SelectTrigger>
                <SelectPopup alignItemWithTrigger={false}>
                  {PAIRING_MAX_USES_OPTIONS.map((maxUses) => (
                    <SelectItem hideIndicator key={maxUses} value={String(maxUses)}>
                      {plural(maxUses, { one: "# device", other: "# devices" })}
                    </SelectItem>
                  ))}
                  <SelectItem hideIndicator value="unlimited">
                    <Trans>Any number of devices</Trans>
                  </SelectItem>
                </SelectPopup>
              </Select>
              <p className="text-xs text-muted-foreground">
                {pairingMaxUses === null
                  ? t`Every device that opens the link gets its own session.`
                  : plural(pairingMaxUses, {
                      one: "The link stops working after # device has paired with it.",
                      other: "The link stops working after # devices have paired with it.",
                    })}
              </p>
            </section>
            <section className="space-y-1.5">
              <div>
                <h3 className="text-xs font-medium text-foreground">
                  <Trans>Lifetime</Trans>
                </h3>
                <p className="text-xs text-muted-foreground">
                  <Trans>
                    When it ends, the link and every device paired with it stop working.
                  </Trans>
                </p>
              </div>
              <Select
                value={pairingTtlDays === null ? "none" : String(pairingTtlDays)}
                onValueChange={(value) => {
                  setPairingTtlDays(value === "none" ? null : Number(value));
                }}
              >
                <SelectTrigger
                  className="w-full"
                  aria-label={t`Lifetime`}
                  disabled={isCreatingPairingLink}
                >
                  <SelectValue>
                    {pairingTtlDays === null
                      ? t`No expiry`
                      : plural(pairingTtlDays, { one: "# day", other: "# days" })}
                  </SelectValue>
                </SelectTrigger>
                <SelectPopup alignItemWithTrigger={false}>
                  <SelectItem hideIndicator value="none">
                    <Trans>No expiry</Trans>
                  </SelectItem>
                  {PAIRING_TTL_DAY_OPTIONS.map((days) => (
                    <SelectItem hideIndicator key={days} value={String(days)}>
                      {plural(days, { one: "# day", other: "# days" })}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
              <p className="text-xs text-muted-foreground">
                {pairingTtlDays === null
                  ? t`The link and its devices stay valid until the link is revoked.`
                  : t`The link and its devices will expire on ${accessTimestampFormatter.format(
                      new Date(Date.now() + pairingTtlDays * SECONDS_PER_DAY * 1000),
                    )}.`}
              </p>
            </section>
          </DialogPanel>
          <DialogFooter variant="bare">
            <Button
              variant="outline"
              disabled={isCreatingPairingLink}
              onClick={() => setDialogOpen(false)}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              disabled={isCreatingPairingLink || pairingScopes.length === 0}
              onClick={() => void handleCreatePairingLink()}
            >
              {isCreatingPairingLink ? <Trans>Creating…</Trans> : <Trans>Create link</Trans>}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </div>
  );
});

type PairingClientsListProps = {
  isLoading: boolean;
  pairingLinks: ReadonlyArray<ServerPairingLinkRecord>;
  clientSessions: ReadonlyArray<ServerClientSessionRecord>;
  revokingPairingLinkId: string | null;
  revokingClientSessionId: string | null;
  onRevokePairingLink: (id: string) => void;
  onRevokeClientSession: (sessionId: ServerClientSessionRecord["sessionId"]) => void;
};

const PairingClientsList = memo(function PairingClientsList({
  isLoading,
  pairingLinks,
  clientSessions,
  revokingPairingLinkId,
  revokingClientSessionId,
  onRevokePairingLink,
  onRevokeClientSession,
}: PairingClientsListProps) {
  const connectedSubjects = useMemo(
    () =>
      new Set(
        clientSessions
          .filter((clientSession) => clientSession.current || clientSession.connected)
          .map((clientSession) => clientSession.subject),
      ),
    [clientSessions],
  );
  // Personal links carry a unique subject that every session created through
  // them inherits, so sessions nest under their originating link. Sessions
  // with no listed link (one-time tokens, CLI/bootstrap issued) stay flat.
  const { sessionsByLinkSubject, unlinkedSessions } = useMemo(() => {
    const linkSubjects = new Set(pairingLinks.map((pairingLink) => pairingLink.subject));
    const bySubject = new Map<string, ServerClientSessionRecord[]>();
    const unlinked: ServerClientSessionRecord[] = [];
    for (const clientSession of clientSessions) {
      if (linkSubjects.has(clientSession.subject)) {
        const group = bySubject.get(clientSession.subject) ?? [];
        group.push(clientSession);
        bySubject.set(clientSession.subject, group);
      } else {
        unlinked.push(clientSession);
      }
    }
    return { sessionsByLinkSubject: bySubject, unlinkedSessions: unlinked };
  }, [pairingLinks, clientSessions]);

  return (
    <>
      {pairingLinks.map((pairingLink) => (
        <Fragment key={pairingLink.id}>
          <PairingLinkListRow
            pairingLink={pairingLink}
            isConnected={connectedSubjects.has(pairingLink.subject)}
            revokingPairingLinkId={revokingPairingLinkId}
            onRevoke={onRevokePairingLink}
          />
          {(sessionsByLinkSubject.get(pairingLink.subject) ?? []).map((clientSession) => (
            <ConnectedClientListRow
              key={clientSession.sessionId}
              clientSession={clientSession}
              nested
              revokingClientSessionId={revokingClientSessionId}
            />
          ))}
        </Fragment>
      ))}

      {unlinkedSessions.map((clientSession) => (
        <ConnectedClientListRow
          key={clientSession.sessionId}
          clientSession={clientSession}
          revokingClientSessionId={revokingClientSessionId}
          onRevokeSession={onRevokeClientSession}
        />
      ))}

      {pairingLinks.length === 0 && clientSessions.length === 0 && !isLoading ? (
        <div className={ITEM_ROW_CLASSNAME}>
          <p className="text-xs text-muted-foreground/60">
            <Trans>No pairing links or client sessions.</Trans>
          </p>
        </div>
      ) : null}
    </>
  );
});

type SavedBackendListRowProps = {
  environmentId: EnvironmentId;
  reconnectingEnvironmentId: EnvironmentId | null;
  disconnectingEnvironmentId: EnvironmentId | null;
  removingEnvironmentId: EnvironmentId | null;
  onConnect: (environmentId: EnvironmentId) => void;
  onDisconnect: (environmentId: EnvironmentId) => void;
  onRemove: (environmentId: EnvironmentId) => void;
};

function SavedBackendListRow({
  environmentId,
  reconnectingEnvironmentId,
  disconnectingEnvironmentId,
  removingEnvironmentId,
  onConnect,
  onDisconnect,
  onRemove,
}: SavedBackendListRowProps) {
  const { t } = useLingui();
  const nowMs = useRelativeTimeTick(1_000);
  const record = useSavedEnvironmentRegistryStore((state) => state.byId[environmentId] ?? null);
  const runtime = useSavedEnvironmentRuntimeStore((state) => state.byId[environmentId] ?? null);

  if (!record) {
    return null;
  }

  const connectionState = runtime?.connectionState ?? "disconnected";
  const isConnected = connectionState === "connected";
  const isConnecting =
    connectionState === "connecting" || reconnectingEnvironmentId === environmentId;
  const isDisconnecting = disconnectingEnvironmentId === environmentId;
  const stateDotClassName =
    connectionState === "connected"
      ? "bg-success"
      : connectionState === "connecting"
        ? "bg-warning"
        : connectionState === "error"
          ? "bg-destructive"
          : "bg-muted-foreground/40";
  const descriptorLabel = runtime?.descriptor?.label ?? null;
  const displayLabel = descriptorLabel ?? record.label;
  const statusTooltip = getSavedBackendStatusTooltip(runtime, record, nowMs);
  const versionMismatch = resolveServerConfigVersionMismatch(runtime?.serverConfig);
  const metadataBits = [
    record.lastConnectedAt
      ? t`Last connected ${formatAccessTimestamp(record.lastConnectedAt)}`
      : null,
  ].filter((value): value is string => value !== null);

  return (
    <div className={ITEM_ROW_CLASSNAME}>
      <div className={ITEM_ROW_INNER_CLASSNAME}>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-h-5 items-center gap-1.5">
            <ConnectionStatusDot
              tooltipText={statusTooltip}
              dotClassName={stateDotClassName}
              pingClassName={
                connectionState === "connecting" ? "bg-warning/60 duration-2000" : null
              }
            />
            <h3 className="text-sm font-medium text-foreground">{displayLabel}</h3>
          </div>
          {metadataBits.length > 0 || runtime?.scopes ? (
            <p className="text-xs text-muted-foreground">
              {metadataBits.length > 0 ? metadataBits.join(" · ") : null}
              {metadataBits.length > 0 && runtime?.scopes ? <span aria-hidden> · </span> : null}
              {runtime?.scopes ? (
                <AccessScopeSummary scopes={runtime.scopes} label={t`Granted scopes`} />
              ) : null}
            </p>
          ) : null}
          {versionMismatch ? (
            <p className="flex items-center gap-1 text-warning text-xs">
              <TriangleAlertIcon className="size-3.5 shrink-0" />
              <Trans>
                Version drift: client {versionMismatch.clientVersion}, server{" "}
                {versionMismatch.serverVersion}.
              </Trans>
            </p>
          ) : null}
        </div>
        <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto sm:justify-end">
          <Button
            size="xs"
            variant="outline"
            disabled={isConnected ? isDisconnecting : isConnecting}
            onClick={() =>
              void (isConnected ? onDisconnect(environmentId) : onConnect(environmentId))
            }
          >
            {isConnected ? (
              isDisconnecting ? (
                <Trans>Disconnecting…</Trans>
              ) : (
                <Trans>Disconnect</Trans>
              )
            ) : isConnecting ? (
              <Trans>Connecting…</Trans>
            ) : (
              <Trans>Connect</Trans>
            )}
          </Button>
          <Button
            size="xs"
            variant="destructive-outline"
            disabled={removingEnvironmentId === environmentId}
            onClick={() => void onRemove(environmentId)}
          >
            {removingEnvironmentId === environmentId ? (
              <Trans>Removing…</Trans>
            ) : (
              <Trans>Remove</Trans>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmptyRemoteEnvironments() {
  return (
    <Empty className="min-h-52">
      <EmptyMedia variant="icon">
        <ChevronsLeftRightEllipsisIcon />
      </EmptyMedia>
      <EmptyHeader>
        <EmptyTitle>
          <Trans>No saved remote environments</Trans>
        </EmptyTitle>
        <EmptyDescription>
          <Trans>Click “Add environment” to pair another environment.</Trans>
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

export function ConnectionsSettings() {
  const { t } = useLingui();
  const primarySessionState = usePrimarySessionState();
  const currentSessionScopes = primarySessionState.data?.authenticated
    ? (primarySessionState.data.scopes ?? null)
    : null;
  const currentAuthPolicy = primarySessionState.data?.auth.policy ?? null;
  const savedEnvironmentsById = useSavedEnvironmentRegistryStore((state) => state.byId);
  const savedEnvironmentIds = useMemo(
    () =>
      Object.values(savedEnvironmentsById)
        .toSorted((left, right) => left.label.localeCompare(right.label))
        .map((record) => record.environmentId),
    [savedEnvironmentsById],
  );
  const [pairingLinks, setPairingLinks] = useState<ReadonlyArray<ServerPairingLinkRecord>>([]);
  const [clientSessions, setClientSessions] = useState<ReadonlyArray<ServerClientSessionRecord>>(
    [],
  );
  const [accessManagementError, setAccessManagementError] = useState<string | null>(null);
  const [isLoadingAccessManagement, setIsLoadingAccessManagement] = useState(false);
  const [revokingPairingLinkId, setRevokingPairingLinkId] = useState<string | null>(null);
  const [revokingClientSessionId, setRevokingClientSessionId] = useState<string | null>(null);
  const [addBackendDialogOpen, setAddBackendDialogOpen] = useState(false);
  const [savedBackendHost, setSavedBackendHost] = useState("");
  const [savedBackendPairingCode, setSavedBackendPairingCode] = useState("");
  const [savedBackendError, setSavedBackendError] = useState<string | null>(null);
  const [isAddingSavedBackend, setIsAddingSavedBackend] = useState(false);
  const [reconnectingSavedEnvironmentId, setReconnectingSavedEnvironmentId] =
    useState<EnvironmentId | null>(null);
  const [disconnectingSavedEnvironmentId, setDisconnectingSavedEnvironmentId] =
    useState<EnvironmentId | null>(null);
  const [removingSavedEnvironmentId, setRemovingSavedEnvironmentId] =
    useState<EnvironmentId | null>(null);
  const primaryServerConfig = useServerConfig();
  const primaryVersionMismatch = resolveServerConfigVersionMismatch(primaryServerConfig);
  const canManageLocalBackend = currentSessionScopes?.includes(AuthAccessWriteScope) ?? false;
  const isLocalBackendNetworkAccessible = currentAuthPolicy === "remote-reachable";

  const handleRevokePairingLink = useCallback(
    async (id: string) => {
      setRevokingPairingLinkId(id);
      setAccessManagementError(null);
      try {
        await revokeServerPairingLink(id);
      } catch (error) {
        const message = error instanceof Error ? error.message : t`Failed to revoke pairing link.`;
        setAccessManagementError(message);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: t`Could not revoke pairing link`,
            description: message,
          }),
        );
      } finally {
        setRevokingPairingLinkId(null);
      }
    },
    [t],
  );

  const handleRevokeClientSession = useCallback(
    async (sessionId: ServerClientSessionRecord["sessionId"]) => {
      setRevokingClientSessionId(sessionId);
      setAccessManagementError(null);
      try {
        await revokeServerClientSession(sessionId);
      } catch (error) {
        const message = error instanceof Error ? error.message : t`Failed to revoke client access.`;
        setAccessManagementError(message);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: t`Could not revoke client access`,
            description: message,
          }),
        );
      } finally {
        setRevokingClientSessionId(null);
      }
    },
    [t],
  );

  const handleAddSavedBackend = useCallback(async () => {
    setIsAddingSavedBackend(true);
    setSavedBackendError(null);
    try {
      const remotePairingInput = parseRemotePairingFields({
        host: savedBackendHost,
        pairingCode: savedBackendPairingCode,
      });
      const record = await addSavedEnvironment({
        label: "",
        ...remotePairingInput,
      });
      setSavedBackendHost("");
      setSavedBackendPairingCode("");
      setAddBackendDialogOpen(false);
      toastManager.add({
        type: "success",
        title: t`Backend added`,
        description: t`${record.label} is now saved and will reconnect on app startup.`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to add backend.`;
      setSavedBackendError(message);
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: t`Could not add backend`,
          description: message,
        }),
      );
    } finally {
      setIsAddingSavedBackend(false);
    }
  }, [savedBackendHost, savedBackendPairingCode, t]);

  const handleConnectSavedBackend = useCallback(
    async (environmentId: EnvironmentId) => {
      setReconnectingSavedEnvironmentId(environmentId);
      setSavedBackendError(null);
      try {
        await reconnectSavedEnvironment(environmentId);
      } catch (error) {
        const message = error instanceof Error ? error.message : t`Failed to connect backend.`;
        setSavedBackendError(message);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: t`Could not connect backend`,
            description: message,
          }),
        );
      } finally {
        setReconnectingSavedEnvironmentId(null);
      }
    },
    [t],
  );

  const handleDisconnectSavedBackend = useCallback(
    async (environmentId: EnvironmentId) => {
      setDisconnectingSavedEnvironmentId(environmentId);
      setSavedBackendError(null);
      try {
        await disconnectSavedEnvironment(environmentId);
      } catch (error) {
        const message = error instanceof Error ? error.message : t`Failed to disconnect backend.`;
        setSavedBackendError(message);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: t`Could not disconnect backend`,
            description: message,
          }),
        );
      } finally {
        setDisconnectingSavedEnvironmentId(null);
      }
    },
    [t],
  );

  const handleRemoveSavedBackend = useCallback(
    async (environmentId: EnvironmentId) => {
      setRemovingSavedEnvironmentId(environmentId);
      setSavedBackendError(null);
      try {
        await removeSavedEnvironment(environmentId);
      } catch (error) {
        const message = error instanceof Error ? error.message : t`Failed to remove backend.`;
        setSavedBackendError(message);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: t`Could not remove backend`,
            description: message,
          }),
        );
      } finally {
        setRemovingSavedEnvironmentId(null);
      }
    },
    [t],
  );

  useEffect(() => {
    if (!canManageLocalBackend) return;

    let cancelled = false;
    setIsLoadingAccessManagement(true);
    type AuthAccessEvent = Parameters<
      Parameters<WsRpcClient["server"]["subscribeAuthAccess"]>[0]
    >[0];
    const unsubscribeAuthAccess =
      getPrimaryEnvironmentConnection().client.server.subscribeAuthAccess(
        (event: AuthAccessEvent) => {
          if (cancelled) {
            return;
          }

          switch (event.type) {
            case "snapshot":
              setPairingLinks(
                sortPairingLinks(
                  event.payload.pairingLinks.map((pairingLink: AuthPairingLink) =>
                    toPairingLinkRecord(pairingLink),
                  ),
                ),
              );
              setClientSessions(
                sortClientSessions(
                  event.payload.clientSessions.map((clientSession: AuthClientSession) =>
                    toClientSessionRecord(clientSession),
                  ),
                ),
              );
              break;
            case "pairingLinkUpserted":
              setPairingLinks((current) =>
                upsertPairingLink(current, toPairingLinkRecord(event.payload)),
              );
              break;
            case "pairingLinkRemoved":
              setPairingLinks((current) => removePairingLink(current, event.payload.id));
              break;
            case "clientUpserted":
              setClientSessions((current) =>
                upsertClientSession(current, toClientSessionRecord(event.payload)),
              );
              break;
            case "clientRemoved":
              setClientSessions((current) => removeClientSession(current, event.payload.sessionId));
              break;
          }

          setAccessManagementError(null);
          setIsLoadingAccessManagement(false);
        },
        {
          onResubscribe: () => {
            if (!cancelled) {
              setIsLoadingAccessManagement(true);
            }
          },
        },
      );
    return () => {
      cancelled = true;
      unsubscribeAuthAccess();
    };
  }, [canManageLocalBackend, t]);

  useEffect(() => {
    if (canManageLocalBackend) return;
    setIsLoadingAccessManagement(false);
    setPairingLinks([]);
    setClientSessions([]);
    setAccessManagementError(null);
  }, [canManageLocalBackend]);
  const handleSavedBackendHostChange = useCallback((value: string) => {
    const parsedPairingUrl = parsePairingUrlFields(value);
    if (parsedPairingUrl) {
      setSavedBackendHost(parsedPairingUrl.host);
      setSavedBackendPairingCode(parsedPairingUrl.pairingCode);
      return;
    }
    setSavedBackendHost(value);
  }, []);

  const renderRemoteFields = () => (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_10rem]">
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-foreground">
            <Trans>Host</Trans>
          </span>
          <Input
            value={savedBackendHost}
            onChange={(event) => handleSavedBackendHostChange(event.target.value)}
            placeholder="backend.example.com"
            disabled={isAddingSavedBackend}
            spellCheck={false}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-foreground">
            <Trans>Pairing code</Trans>
          </span>
          <Input
            value={savedBackendPairingCode}
            onChange={(event) => setSavedBackendPairingCode(event.target.value)}
            placeholder="PAIRCODE"
            disabled={isAddingSavedBackend}
            spellCheck={false}
          />
        </label>
      </div>
      <div>
        <span className="mt-1 block text-[11px] text-muted-foreground">
          <Trans>Paste a full pairing URL here to fill both fields automatically.</Trans>
        </span>
      </div>
    </div>
  );
  const renderRemoteModeBody = () => (
    <div className="space-y-4">
      {renderRemoteFields()}
      {savedBackendError ? <p className="text-xs text-destructive">{savedBackendError}</p> : null}
      <Button
        variant="outline"
        className="w-full"
        disabled={isAddingSavedBackend}
        onClick={() => void handleAddSavedBackend()}
      >
        <PlusIcon className="size-3.5" />
        {isAddingSavedBackend ? <Trans>Adding…</Trans> : <Trans>Add environment</Trans>}
      </Button>
    </div>
  );
  const renderAuthorizedClients = () => (
    <>
      {accessManagementError ? (
        <div className={ITEM_ROW_CLASSNAME}>
          <p className="text-xs text-destructive">{accessManagementError}</p>
        </div>
      ) : null}
      <PairingClientsList
        isLoading={isLoadingAccessManagement}
        pairingLinks={pairingLinks}
        clientSessions={clientSessions}
        revokingPairingLinkId={revokingPairingLinkId}
        revokingClientSessionId={revokingClientSessionId}
        onRevokePairingLink={handleRevokePairingLink}
        onRevokeClientSession={handleRevokeClientSession}
      />
    </>
  );
  const renderNetworkAccessRow = () => (
    <SettingsRow
      title={t`Network access`}
      description={
        currentAuthPolicy === "remote-reachable"
          ? t`This backend is already configured for remote access. Network exposure changes must be made where the server is launched.`
          : t`This backend is only reachable on this machine. Restart it with a non-loopback host to enable remote pairing.`
      }
      control={
        <Tooltip>
          <TooltipTrigger
            render={
              <span className="inline-flex">
                <Switch
                  checked={isLocalBackendNetworkAccessible}
                  disabled
                  aria-label={t`Enable network access`}
                />
              </span>
            }
          />
          <TooltipPopup side="top">
            <Trans>
              Network exposure changes restart the backend and must be controlled where the server
              process is launched.
            </Trans>
          </TooltipPopup>
        </Tooltip>
      }
    />
  );

  return (
    <SettingsPageContainer>
      {canManageLocalBackend ? (
        <>
          <SettingsSection title={t`This environment`}>
            {primaryVersionMismatch ? (
              <SettingsRow
                title={t`Version drift`}
                description={
                  <span className="flex items-center gap-1 text-warning">
                    <TriangleAlertIcon className="size-3.5 shrink-0" />
                    <Trans>
                      Client {primaryVersionMismatch.clientVersion}, server{" "}
                      {primaryVersionMismatch.serverVersion}. Sync them if RPC calls or reconnects
                      fail.
                    </Trans>
                  </span>
                }
              />
            ) : null}
            {renderNetworkAccessRow()}
          </SettingsSection>

          {isLocalBackendNetworkAccessible ? (
            <SettingsSection
              title={t`Authorized clients`}
              headerAction={<AuthorizedClientsHeaderAction />}
            >
              {renderAuthorizedClients()}
            </SettingsSection>
          ) : null}
        </>
      ) : (
        <SettingsSection title={t`This environment`}>
          <SettingsRow
            title={t`Administrative access`}
            description={t`Pairing links and client-session management require the access:write scope for this backend.`}
          />
        </SettingsSection>
      )}

      <SettingsSection
        title={t`Remote environments`}
        headerAction={
          <Dialog
            open={addBackendDialogOpen}
            onOpenChange={(open) => {
              setAddBackendDialogOpen(open);
              if (!open) {
                setSavedBackendError(null);
              }
            }}
          >
            <Tooltip>
              <TooltipTrigger
                render={
                  <DialogTrigger
                    render={
                      <Button
                        size="xs"
                        variant="ghost"
                        className="h-5 gap-1 rounded-sm px-1 text-[11px] font-normal text-muted-foreground/60 hover:text-muted-foreground"
                        aria-label={t`Add environment`}
                      >
                        <PlusIcon className="size-3" />
                        <span>
                          <Trans>Add environment</Trans>
                        </span>
                      </Button>
                    }
                  />
                }
              />
              <TooltipPopup side="top">
                <Trans>Add environment</Trans>
              </TooltipPopup>
            </Tooltip>
            <DialogPopup className="max-h-[80dvh] sm:max-w-3xl">
              <DialogHeader>
                <DialogTitle>
                  <Trans>Add Environment</Trans>
                </DialogTitle>
                <DialogDescription>
                  <Trans>Pair another environment to this client.</Trans>
                </DialogDescription>
              </DialogHeader>
              <DialogPanel>{renderRemoteModeBody()}</DialogPanel>
            </DialogPopup>
          </Dialog>
        }
      >
        {savedEnvironmentIds.map((environmentId) => (
          <SavedBackendListRow
            key={environmentId}
            environmentId={environmentId}
            reconnectingEnvironmentId={reconnectingSavedEnvironmentId}
            disconnectingEnvironmentId={disconnectingSavedEnvironmentId}
            removingEnvironmentId={removingSavedEnvironmentId}
            onConnect={handleConnectSavedBackend}
            onDisconnect={handleDisconnectSavedBackend}
            onRemove={handleRemoveSavedBackend}
          />
        ))}
        {savedEnvironmentIds.length === 0 ? <EmptyRemoteEnvironments /> : null}
      </SettingsSection>
    </SettingsPageContainer>
  );
}
